import { FC, ReactNode, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '@components/atoms/Badge';
import { Card } from '@components/atoms/Card';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';

interface ProjectReportCardProps {
  title: string;
  /** Attention badge (e.g. "2 incidents"); omitted when empty. */
  badge?: string;
  children: ReactNode;
}

/** One collapsible card (a project, the trailing Stock card, or the looking-ahead block). Expanded
 * by default; the header is a real button (aria-expanded), local state only. */
export const ProjectReportCard: FC<ProjectReportCardProps> = ({ title, badge, children }) => {
  const t = useTranslation('dailySiteReport');
  const [expanded, setExpanded] = useState(true);
  return (
    <Card className="p-4 mb-4" data-testid="project-report-card">
      <button
        type="button"
        aria-expanded={expanded}
        aria-label={t.cardToggleAria.replace('{project}', title)}
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">
        <span className="text-lg font-semibold text-gray-900 dark:text-white">{title}</span>
        <span className="flex items-center gap-2">
          {badge && (
            <Badge size="sm" variant="warning">
              {badge}
            </Badge>
          )}
          <ChevronDown className={cn('w-5 h-5 transition-transform', expanded && 'rotate-180')} />
        </span>
      </button>
      {expanded && <div className="mt-2 border-t border-gray-200 dark:border-gray-700 pt-3">{children}</div>}
    </Card>
  );
};

export default ProjectReportCard;
